import { useEffect, useRef } from 'react';
import { track } from '../../../utils/track';

export type ReplayHold = 'rewind' | 'fastForward';

// The speed buttons and the −/+ keys step through the same list.
export const REPLAY_SPEEDS = [0.25, 0.5, 1, 2, 4];

export interface ReplayShortcutHandlers {
    togglePlay: () => void;
    seekSeconds: (seconds: number) => void;
    stepFrame: (delta: number) => void;
    changeRound: (delta: number) => void;
    changeSpeed: (delta: number) => void;
    jumpToRoundStart: () => void;
    copyLink: () => void;
    toggleFullscreen: () => void;
    resetView: () => void;
    toggleHelp: () => void;
    // Returns true when there was something to close, so Escape is only swallowed then.
    closeHelp: () => boolean;
    startHold: (hold: ReplayHold) => void;
    endHold: () => void;
}

// What the help overlay lists. Kept beside the key handling below so the two do not drift.
// `copyLink` is hidden where the viewer has no shareable URL.
export const REPLAY_SHORTCUTS: { keys: string[]; label: string; copyLink?: boolean }[] = [
    { keys: ['Space'], label: 'Play / pause' },
    { keys: ['←', '→'], label: 'Back / forward 5 seconds' },
    { keys: ['Shift+←', 'Shift+→'], label: 'Back / forward 1 second' },
    { keys: [',', '.'], label: 'Previous / next frame' },
    { keys: ['↑', '↓'], label: 'Next / previous round' },
    { keys: ['Q'], label: 'Rewind (hold)' },
    { keys: ['E'], label: 'Fast-forward 2x (hold)' },
    { keys: ['−', '+'], label: 'Slower / faster' },
    { keys: ['Home'], label: 'Round start' },
    { keys: ['L'], label: 'Copy link to this moment', copyLink: true },
    { keys: ['F'], label: 'Fullscreen' },
    { keys: ['R'], label: 'Reset zoom' },
    { keys: ['?'], label: 'Show shortcuts' },
];

// Letters are matched on e.code so they stay on the same physical key on any layout. Symbols are
// matched on e.key, and none of them need AltGr on a Danish keyboard.
export function useReplayShortcuts(enabled: boolean, handlers: ReplayShortcutHandlers) {
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    const holdCodeRef = useRef<string | null>(null);
    const trackedRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        // A hold has to end even when its keyup never arrives: alt-tab, or the mouse leaving the
        // viewer (which closes the gate) would otherwise leave playback rewinding.
        const endHold = () => {
            if (holdCodeRef.current === null) return;
            holdCodeRef.current = null;
            handlersRef.current.endHold();
        };
        if (!enabled) {
            endHold();
            return;
        }

        const used = (action: string) => {
            if (trackedRef.current.has(action)) return;
            trackedRef.current.add(action);
            track('replay-shortcut', { action });
        };

        const onKeyDown = (e: KeyboardEvent) => {
            // Leave browser shortcuts (Ctrl+R, Ctrl+F, Cmd+...) alone.
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
            const h = handlersRef.current;

            if (e.code === 'KeyQ' || e.code === 'KeyE') {
                e.preventDefault();
                if (e.repeat || holdCodeRef.current !== null) return;
                const hold: ReplayHold = e.code === 'KeyQ' ? 'rewind' : 'fastForward';
                holdCodeRef.current = e.code;
                h.startHold(hold);
                used(hold);
                return;
            }

            // Seeks and frame steps repeat while held; toggles and round changes do not.
            let action: string | null = null;
            let repeats = false;
            let run: () => void = () => {};
            if (e.code === 'Space' || e.key === ' ') {
                action = 'playPause'; run = h.togglePlay;
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                const dir = e.key === 'ArrowLeft' ? -1 : 1;
                action = 'seek'; repeats = true; run = () => h.seekSeconds(dir * (e.shiftKey ? 1 : 5));
            } else if (e.key === ',' || e.key === '.') {
                const dir = e.key === ',' ? -1 : 1;
                action = 'step'; repeats = true; run = () => h.stepFrame(dir);
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                const dir = e.key === 'ArrowUp' ? 1 : -1;
                action = 'round'; run = () => h.changeRound(dir);
            } else if (e.key === '-' || e.key === '+' || e.key === '=') {
                const dir = e.key === '-' ? -1 : 1;
                action = 'speed'; run = () => h.changeSpeed(dir);
            } else if (e.key === 'Home') {
                action = 'roundStart'; run = h.jumpToRoundStart;
            } else if (e.code === 'KeyL') {
                action = 'copyLink'; run = h.copyLink;
            } else if (e.code === 'KeyF') {
                action = 'fullscreen'; run = h.toggleFullscreen;
            } else if (e.code === 'KeyR') {
                action = 'resetView'; run = h.resetView;
            } else if (e.key === '?') {
                action = 'help'; run = h.toggleHelp;
            } else if (e.key === 'Escape') {
                if (h.closeHelp()) e.preventDefault();
                return;
            }
            if (action === null) return;

            e.preventDefault();
            if (e.repeat && !repeats) return;
            run();
            used(action);
        };

        const onKeyUp = (e: KeyboardEvent) => {
            if (e.code === holdCodeRef.current) endHold();
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', endHold);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', endHold);
            endHold();
        };
    }, [enabled]);
}
